'use strict';

/**
 * T451 P3.4 — the task MUTATIONS, shared by the SERVER (index.js delegates to
 * these) and the BROWSER (the sealed write path applies a mutation to the store
 * it unsealed, then seals the whole store — shared/cores/tasks/task-store-unseal.js,
 * delivered through the vault bundle).
 *
 * Moved VERBATIM out of index.js (clampIndex :6628 … deleteUserTaskList :7300 at
 * 47f311a82). index.js keeps one-line delegates under the same names, so every
 * existing call site and export is unchanged and there is ONE implementation,
 * not a server copy and a browser twin that drift apart (the P3.1 task-state.js
 * shape).
 *
 * Two edges were server-bound and are INJECTED through `options`, never reached
 * for:
 *   options.onTaskStatus(task, done, options) — the generator notify on a
 *     completion flip (index.js `notifyGeneratorTaskStatus`: forge closes the
 *     issue). Absent ⇒ no notification (the browser has no generator registry;
 *     the sealed write route re-derives nothing from ciphertext — see the lane
 *     record for the named remainder).
 *   options.workGraph = { build(payload), apply(target, payload) } — the
 *     work-graph FK resolvers (work-graph-refs.js needs node:path + the tid
 *     grammar; not browser-safe). The DEFAULT below REFUSES any ref it cannot
 *     resolve — a dangling edge is the defect T1444P exists to fix — and allows
 *     an explicit clear (null / ''), which needs no resolution.
 *
 * BROWSER-SAFE: `node:crypto` is aliased to the esbuild crypto shim in the vault
 * bundle (randomUUID is Web-Crypto backed there); no fs, no env.
 */

const { randomUUID } = require('node:crypto');
const { ensureUserTaskState } = require('./task-state.js');

// Pinned to work-graph-refs.js `WORK_GRAPH_REF_FIELDS` by
// __tests__/tasks/t451-p3-4-tasks-sealed-write-v1.test.js P1 — a twin that
// cannot drift, because that module cannot be required from the browser.
const WORK_GRAPH_REF_FIELDS = Object.freeze(['arcTid', 'docRef', 'submissionId']);

function isClearRef(v) { return v === null || v === ''; }

function refuseUnresolvableRefs(payload) {
  for (const field of WORK_GRAPH_REF_FIELDS) {
    const v = payload ? payload[field] : undefined;
    if (v !== undefined && !isClearRef(v)) {
      const err = new Error('work-graph ref "' + field + '" cannot be resolved here — resolving an edge needs the server '
        + '(shared/cores/tasks/work-graph-refs.js); a dangling edge is refused, never stored');
      err.code = 'ERR_WORK_GRAPH_REF_UNRESOLVABLE';
      throw err;
    }
  }
}

const DEFAULT_WORK_GRAPH = Object.freeze({
  build(payload = {}) {
    refuseUnresolvableRefs(payload);
    const refs = {};
    for (const field of WORK_GRAPH_REF_FIELDS) refs[field] = null;
    return refs;
  },
  apply(target, payload = {}) {
    refuseUnresolvableRefs(payload);
    let mutated = false;
    for (const field of WORK_GRAPH_REF_FIELDS) {
      if (payload[field] === undefined) continue;
      const current = target[field] === undefined ? null : target[field];
      if (current !== null) { target[field] = null; mutated = true; }
    }
    return mutated;
  },
});

function workGraphOf(options) {
  const wg = options && options.workGraph;
  return (wg && typeof wg.build === 'function' && typeof wg.apply === 'function') ? wg : DEFAULT_WORK_GRAPH;
}

function clampIndex(index, length) {
  if (!Number.isFinite(index)) return length;
  if (index < 0) return 0;
  if (index > length) return length;
  return index;
}

function removeFromArray(list, value) {
  if (!Array.isArray(list)) return false;
  const index = list.indexOf(value);
  if (index === -1) return false;
  list.splice(index, 1);
  return true;
}

// T122 LD-13: Routine nesting depth cap (4 levels max)
const MAX_ROUTINE_DEPTH = 4;

function getRoutineAncestorDepth(userState, parentTaskId) {
  let depth = 0;
  let current = parentTaskId;
  while (current && depth <= MAX_ROUTINE_DEPTH) {
    depth++;
    const t = userState.tasks && userState.tasks[current];
    if (!t) break;
    current = t.parentTaskId || null;
  }
  return depth;
}

function isListDescendant(userState, candidateId, ancestorId) {
  if (!candidateId || !ancestorId || candidateId === ancestorId) return candidateId === ancestorId;
  let current = userState.lists?.[candidateId] || null;
  while (current) {
    if (current.parentId === ancestorId || current.id === ancestorId) {
      return true;
    }
    if (!current.parentId) break;
    current = userState.lists?.[current.parentId] || null;
  }
  return false;
}

function isTaskDescendant(userState, candidateId, ancestorId) {
  if (!candidateId || !ancestorId) return false;
  if (candidateId === ancestorId) return true;
  let current = userState.tasks?.[candidateId] || null;
  while (current) {
    if (current.parentTaskId === ancestorId || current.id === ancestorId) {
      return true;
    }
    if (!current.parentTaskId) break;
    current = userState.tasks?.[current.parentTaskId] || null;
  }
  return false;
}

function ensureListCollections(list) {
  if (!Array.isArray(list.childListIds)) {
    list.childListIds = [];
  }
  if (!Array.isArray(list.taskIds)) {
    list.taskIds = [];
  }
}

function ensureTaskCollections(task) {
  if (!Array.isArray(task.childTaskIds)) {
    task.childTaskIds = [];
  }
}

// T469 LD-14 — list provenance is SERVER-SET only. A client may NOT supply a
// `source` or `generator` field (that would let a member mint a list claiming
// auto:master-todo). Both create + update reject any client-supplied value.
function rejectClientProvenance(payload) {
  if (payload && (payload.source !== undefined || payload.generator !== undefined)) {
    throw new Error('List provenance (source/generator) is server-assigned and cannot be set by the client.');
  }
}

// T469 LD-14 — server-stamped provenance for a user-created (manual) list.
function manualListSource(ownerKey) {
  return { kind: 'manual', generator: null, sourceLabel: 'Manual · ' + String(ownerKey), lastSyncedAt: null };
}

function createUserTaskList(store, ownerKey, payload = {}, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  rejectClientProvenance(payload);
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const name = typeof payload.name === 'string' ? payload.name.trim() : '';
  if (!name) {
    throw new Error('List name required.');
  }
  const parentIdRaw = payload.parentId != null ? String(payload.parentId).trim() : null;
  let parent = null;
  if (parentIdRaw) {
    parent = userState.lists[parentIdRaw];
    if (!parent) {
      throw new Error('Parent list not found.');
    }
    ensureListCollections(parent);
  }
  const id = randomUUID();
  const now = new Date().toISOString();
  const completed = Boolean(payload.isCompleted);
  const list = {
    id,
    name,
    parentId: parent ? parent.id : null,
    childListIds: [],
    taskIds: [],
    scheduledAt: payload.scheduledAt || null,  // ISO datetime — when this list is scheduled
    deadline: payload.deadline || null,        // ISO datetime — due date/time
    alarmAt: payload.alarmAt || null,          // ISO datetime — reminder alarm
    createdAt: now,
    updatedAt: now,
    lastUsedAt: now,
    isCompleted: completed,
    completedAt: completed ? now : null,
    // T469 LD-14 — server-set provenance (manual). Auto-lists are produced by
    // the operator-gated master-todo generator, never created through this path.
    source: manualListSource(ownerKey),
  };
  userState.lists[id] = list;
  const positionRaw = Number(payload.position);
  const position = Number.isFinite(positionRaw) ? Math.trunc(positionRaw) : null;
  const order = parent ? parent.childListIds : userState.rootListIds;
  const targetIndex = clampIndex(position ?? order.length, order.length);
  order.splice(targetIndex, 0, id);
  if (parent) {
    parent.updatedAt = now;
    parent.lastUsedAt = now;
  }
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  return { list, userState };
}

function createUserTask(store, ownerKey, payload = {}, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const title = typeof payload.title === 'string' ? payload.title.trim() : '';
  if (!title) {
    throw new Error('Task title required.');
  }
  const description = typeof payload.description === 'string' ? payload.description : '';
  const parentTaskIdRaw = payload.parentTaskId != null ? String(payload.parentTaskId).trim() : null;
  const listIdRaw = payload.listId != null ? String(payload.listId).trim() : null;
  let parentTask = null;
  let parentList = null;
  let resolvedListId = null;
  if (parentTaskIdRaw) {
    parentTask = userState.tasks[parentTaskIdRaw];
    if (!parentTask) {
      throw new Error('Parent task not found.');
    }
    ensureTaskCollections(parentTask);
    resolvedListId = parentTask.listId;
  }
  if (listIdRaw) {
    parentList = userState.lists[listIdRaw];
    if (!parentList) {
      throw new Error('Parent list not found.');
    }
    ensureListCollections(parentList);
    if (!parentTask) {
      resolvedListId = parentList.id;
    }
  }
  if (!parentTask && !parentList) {
    throw new Error('Parent list or task required.');
  }
  if (parentTask && parentList && parentTask.listId !== parentList.id) {
    throw new Error('Parent task and list mismatch.');
  }
  const now = new Date().toISOString();
  // T122 LD-3/LD-4: Routine depth gate
  const validTypes = new Set(['task', 'routine', 'round']);
  const taskType = validTypes.has(payload.type) ? payload.type : 'task';
  if ((taskType === 'routine' || taskType === 'round') && parentTaskIdRaw) {
    const ancestorDepth = getRoutineAncestorDepth(userState, parentTaskIdRaw);
    if (ancestorDepth + 1 > MAX_ROUTINE_DEPTH) {
      throw new Error('Routine nesting depth exceeds maximum of ' + MAX_ROUTINE_DEPTH + ' levels.');
    }
  }
  const targetCount = (payload.targetCount !== undefined && payload.targetCount !== null && payload.targetCount > 0)
    ? Math.trunc(Number(payload.targetCount)) : null;
  const currentCount = 0;

  const task = {
    id: randomUUID(),
    title,
    description,
    listId: resolvedListId,
    parentTaskId: parentTask ? parentTask.id : null,
    childTaskIds: [],
    createdAt: now,
    updatedAt: now,
    lastUsedAt: now,
    isCompleted: Boolean(payload.isCompleted),
    completedAt: payload.isCompleted ? now : null,
    // T122 LD-3/LD-4: Routine subtype fields
    type: taskType,
    targetCount,
    currentCount,
    // T123 W1 LD-2: RRULE recurrence fields (RFC 5545 string + IANA TZ name)
    rrule: payload.rrule || null,
    rruleTzid: payload.rruleTzid || null,
    // T1444P P3 (LD-3): work-graph foreign keys. All three keys are always present (null when
    // unset) so a record's shape does not depend on which edges happened to be supplied here.
    // Throws on an unresolvable ref — a dangling edge is the defect this whole arc exists to fix.
    ...workGraphOf(options).build(payload),
  };
  ensureTaskCollections(task);
  userState.tasks[task.id] = task;
  const order = parentTask ? parentTask.childTaskIds : parentList.taskIds;
  const positionRaw = Number(payload.position);
  const position = Number.isFinite(positionRaw) ? Math.trunc(positionRaw) : null;
  const targetIndex = clampIndex(position ?? order.length, order.length);
  order.splice(targetIndex, 0, task.id);
  if (parentTask) {
    parentTask.updatedAt = now;
    parentTask.lastUsedAt = now;
  }
  if (parentList) {
    parentList.updatedAt = now;
    parentList.lastUsedAt = now;
  }
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  return { task, userState };
}

// T320 P3 (LD-3): instantiate a routine + its rounds ATOMICALLY in-memory
// (one parent + N round children, all on the SAME store object). The caller
// performs a SINGLE store write afterward — replacing the client's
// N+1 POST chain. Rounds are tap-`+1` counters (type:'round', targetCount).
function createUserRoutine(store, ownerKey, payload = {}, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const inner = { fallbackKeys, workGraph: options?.workGraph, onTaskStatus: options?.onTaskStatus };
  const name = typeof payload.name === 'string' ? payload.name.trim()
    : (typeof payload.title === 'string' ? payload.title.trim() : '');
  if (!name) {
    throw new Error('Routine name required.');
  }
  const listId = payload.listId || null;
  const { task: routine } = createUserTask(store, ownerKey, {
    title: name,
    description: payload.description || '',
    listId,
    type: 'routine',
    targetCount: payload.targetCount || null,
  }, inner);

  const rounds = Array.isArray(payload.rounds) ? payload.rounds : [];
  for (const spec of rounds) {
    const roundTitle = typeof spec?.title === 'string' ? spec.title.trim()
      : (typeof spec?.name === 'string' ? spec.name.trim() : '');
    if (!roundTitle) continue;
    createUserTask(store, ownerKey, {
      title: roundTitle,
      description: spec.description || '',
      parentTaskId: routine.id,
      type: 'round',
      targetCount: spec.targetCount || null,
    }, inner);
  }
  return { routine };
}

function updateUserTaskList(store, ownerKey, listId, payload = {}, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  rejectClientProvenance(payload); // T469 LD-14 — provenance is immutable from the client
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.lists[listId];
  if (!target) {
    throw new Error('List not found.');
  }
  ensureListCollections(target);
  const now = new Date().toISOString();
  let mutated = false;
  if (payload.name !== undefined) {
    const name = typeof payload.name === 'string' ? payload.name.trim() : '';
    if (!name) {
      throw new Error('List name required.');
    }
    if (name !== target.name) {
      target.name = name;
      mutated = true;
    }
  }

  if (payload.isCompleted !== undefined) {
    const completed = Boolean(payload.isCompleted);
    if (completed !== Boolean(target.isCompleted)) {
      target.isCompleted = completed;
      target.completedAt = completed ? now : null;
      mutated = true;
    }
  }

  // Schedule fields
  if (payload.scheduledAt !== undefined) {
    target.scheduledAt = payload.scheduledAt || null;
    mutated = true;
  }
  if (payload.deadline !== undefined) {
    target.deadline = payload.deadline || null;
    mutated = true;
  }
  if (payload.alarmAt !== undefined) {
    target.alarmAt = payload.alarmAt || null;
    mutated = true;
  }

  let nextParentId = target.parentId;
  if (payload.parentId !== undefined) {
    const parentIdRaw = payload.parentId != null ? String(payload.parentId).trim() : null;
    if (parentIdRaw === target.id) {
      throw new Error('List cannot be its own parent.');
    }
    if (parentIdRaw && isListDescendant(userState, parentIdRaw, target.id)) {
      throw new Error('List cannot be moved into its descendant.');
    }
    if (parentIdRaw) {
      const parent = userState.lists[parentIdRaw];
      if (!parent) {
        throw new Error('Parent list not found.');
      }
      ensureListCollections(parent);
      nextParentId = parent.id;
    } else {
      nextParentId = null;
    }
  }

  const position = payload.position !== undefined ? Number(payload.position) : null;
  const normalizedPosition = Number.isFinite(position) ? Math.trunc(position) : null;
  const oldParent = target.parentId ? userState.lists[target.parentId] || null : null;
  if (oldParent) ensureListCollections(oldParent);
  const newParent = nextParentId ? userState.lists[nextParentId] || null : null;
  if (newParent) ensureListCollections(newParent);
  const fromOrder = oldParent ? oldParent.childListIds : userState.rootListIds;
  const toOrder = newParent ? newParent.childListIds : userState.rootListIds;
  const sameParent = (oldParent ? oldParent.id : null) === (newParent ? newParent.id : null);

  if (!sameParent) {
    removeFromArray(fromOrder, target.id);
    const insertionIndex = clampIndex(normalizedPosition ?? toOrder.length, toOrder.length);
    toOrder.splice(insertionIndex, 0, target.id);
    target.parentId = newParent ? newParent.id : null;
    mutated = true;
    if (oldParent) {
      oldParent.updatedAt = now;
      oldParent.lastUsedAt = now;
    }
    if (newParent) {
      newParent.updatedAt = now;
      newParent.lastUsedAt = now;
    }
  } else if (normalizedPosition != null) {
    const currentIndex = toOrder.indexOf(target.id);
    if (currentIndex !== -1) {
      toOrder.splice(currentIndex, 1);
      const insertionIndex = clampIndex(normalizedPosition, toOrder.length);
      toOrder.splice(insertionIndex, 0, target.id);
      mutated = true;
    }
  }

  if (mutated) {
    target.updatedAt = now;
    target.lastUsedAt = now;
    userState.updatedAt = now;
    userState.lastUsedAt = now;
  }
  return { list: target, userState, mutated };
}

function updateUserTask(store, ownerKey, taskId, payload = {}, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.tasks[taskId];
  if (!target) {
    throw new Error('Task not found.');
  }
  ensureTaskCollections(target);
  const now = new Date().toISOString();
  let mutated = false;

  if (payload.title !== undefined) {
    const title = typeof payload.title === 'string' ? payload.title.trim() : '';
    if (!title) {
      throw new Error('Task title required.');
    }
    if (title !== target.title) {
      target.title = title;
      mutated = true;
    }
  }

  if (payload.description !== undefined) {
    const description = typeof payload.description === 'string' ? payload.description : '';
    if (description !== target.description) {
      target.description = description;
      mutated = true;
    }
  }

  if (payload.isCompleted !== undefined) {
    const completed = Boolean(payload.isCompleted);
    if (completed !== target.isCompleted) {
      target.isCompleted = completed;
      target.completedAt = completed ? now : null;
      mutated = true;
      // T564 P5 (LD-7): a completion flip on a generator-bound task notifies
      // the owning generator (e.g. forge closes the issue). syncOrigin ===
      // the list tag suppresses the echo (concurrency-DB-3). INJECTED (T451
      // P3.4): the server passes notifyGeneratorTaskStatus; the browser passes
      // nothing.
      if (typeof options?.onTaskStatus === 'function') options.onTaskStatus(target, completed, options);
    }
  }

  // T122 LD-3/LD-4: Routine subtype field updates
  if (payload.type !== undefined) {
    const validTypes = new Set(['task', 'routine', 'round']);
    const newType = validTypes.has(payload.type) ? payload.type : 'task';
    if (newType !== (target.type || 'task')) {
      // HIGH-1 fix: re-run depth gate on type→routine/round promotion (mirrors createUserTask enforcement)
      if ((newType === 'routine' || newType === 'round') && target.parentTaskId) {
        const ancestorDepth = getRoutineAncestorDepth(userState, target.parentTaskId);
        if (ancestorDepth + 1 > MAX_ROUTINE_DEPTH) {
          throw new Error('Routine nesting depth exceeds maximum of ' + MAX_ROUTINE_DEPTH + ' levels.');
        }
      }
      target.type = newType;
      mutated = true;
    }
  }
  if (payload.targetCount !== undefined) {
    const tc = payload.targetCount === null || payload.targetCount === 0 ? null
      : Math.trunc(Math.max(0, Number(payload.targetCount)));
    if (tc !== target.targetCount) {
      target.targetCount = tc;
      mutated = true;
    }
  }
  if (payload.currentCount !== undefined) {
    const cc = Math.max(0, Math.trunc(Number(payload.currentCount)));
    const clamped = target.targetCount ? Math.min(cc, target.targetCount) : cc;
    if (clamped !== target.currentCount) {
      target.currentCount = clamped;
      mutated = true;
    }
  }

  // T123 W1 LD-2: RRULE recurrence field updates
  if (payload.rrule !== undefined) {
    target.rrule = payload.rrule || null;
    mutated = true;
  }
  if (payload.rruleTzid !== undefined) {
    target.rruleTzid = payload.rruleTzid || null;
    mutated = true;
  }

  // T1444P P3 (LD-3): work-graph foreign-key updates. An OMITTED key is not an unlink — only an
  // explicit null/'' clears an edge, else a partial update that never mentions arcTid would
  // quietly sever it. Validates ALL refs before writing ANY, so a refusal leaves nothing
  // half-applied (a task linked to its arc but not to its submission is a silently wrong graph).
  if (workGraphOf(options).apply(target, payload)) {
    mutated = true;
  }

  let nextParentTaskId = target.parentTaskId;
  let nextListId = target.listId;
  let newParentTask = nextParentTaskId ? userState.tasks[nextParentTaskId] || null : null;
  let newParentList = !nextParentTaskId ? userState.lists[nextListId] || null : null;

  if (payload.parentTaskId !== undefined) {
    const parentTaskIdRaw = payload.parentTaskId != null ? String(payload.parentTaskId).trim() : null;
    if (parentTaskIdRaw && isTaskDescendant(userState, parentTaskIdRaw, target.id)) {
      throw new Error('Task cannot be moved into its descendant.');
    }
    if (parentTaskIdRaw) {
      newParentTask = userState.tasks[parentTaskIdRaw];
      if (!newParentTask) {
        throw new Error('Parent task not found.');
      }
      ensureTaskCollections(newParentTask);
      nextParentTaskId = newParentTask.id;
      nextListId = newParentTask.listId;
      newParentList = userState.lists[nextListId] || null;
      if (newParentList) {
        ensureListCollections(newParentList);
      }
    } else {
      nextParentTaskId = null;
      newParentTask = null;
    }
  }

  if (payload.listId !== undefined) {
    const listIdRaw = payload.listId != null ? String(payload.listId).trim() : null;
    if (!listIdRaw) {
      throw new Error('List ID required when moving task to list.');
    }
    newParentList = userState.lists[listIdRaw];
    if (!newParentList) {
      throw new Error('Parent list not found.');
    }
    ensureListCollections(newParentList);
    nextListId = newParentList.id;
    if (nextParentTaskId) {
      if (newParentTask && newParentTask.listId !== nextListId) {
        throw new Error('Parent task and list mismatch.');
      }
      if (!newParentTask) {
        throw new Error('Parent task not found.');
      }
    }
  }

  const position = payload.position !== undefined ? Number(payload.position) : null;
  const normalizedPosition = Number.isFinite(position) ? Math.trunc(position) : null;

  const oldParentTask = target.parentTaskId ? userState.tasks[target.parentTaskId] || null : null;
  if (oldParentTask) ensureTaskCollections(oldParentTask);
  const oldParentList = !target.parentTaskId ? userState.lists[target.listId] || null : null;
  if (oldParentList) ensureListCollections(oldParentList);
  const newParentTaskRef = nextParentTaskId ? newParentTask : null;
  const newParentListRef = !nextParentTaskId ? newParentList : null;
  if (newParentTaskRef) ensureTaskCollections(newParentTaskRef);
  if (newParentListRef) ensureListCollections(newParentListRef);
  const fromOrder = oldParentTask ? oldParentTask.childTaskIds : oldParentList ? oldParentList.taskIds : null;
  const toOrder = newParentTaskRef ? newParentTaskRef.childTaskIds : newParentListRef ? newParentListRef.taskIds : null;
  const sameContainer =
    (oldParentTask && newParentTaskRef && oldParentTask.id === newParentTaskRef.id)
    || (!oldParentTask && !newParentTaskRef && oldParentList && newParentListRef && oldParentList.id === newParentListRef.id);

  if (!sameContainer && fromOrder) {
    removeFromArray(fromOrder, target.id);
  }

  if (!sameContainer && toOrder) {
    const insertionIndex = clampIndex(normalizedPosition ?? toOrder.length, toOrder.length);
    toOrder.splice(insertionIndex, 0, target.id);
    mutated = true;
  } else if (sameContainer && normalizedPosition != null && toOrder) {
    const currentIndex = toOrder.indexOf(target.id);
    if (currentIndex !== -1) {
      toOrder.splice(currentIndex, 1);
      const insertionIndex = clampIndex(normalizedPosition, toOrder.length);
      toOrder.splice(insertionIndex, 0, target.id);
      mutated = true;
    }
  }

  if (nextParentTaskId !== target.parentTaskId) {
    target.parentTaskId = nextParentTaskId;
    mutated = true;
  }
  if (nextListId !== target.listId) {
    target.listId = nextListId;
    mutated = true;
  }

  if (mutated) {
    target.updatedAt = now;
    target.lastUsedAt = now;
    if (newParentTaskRef) {
      newParentTaskRef.updatedAt = now;
      newParentTaskRef.lastUsedAt = now;
    }
    if (newParentListRef) {
      newParentListRef.updatedAt = now;
      newParentListRef.lastUsedAt = now;
    }
    if (oldParentTask && oldParentTask !== newParentTaskRef) {
      oldParentTask.updatedAt = now;
      oldParentTask.lastUsedAt = now;
    }
    if (oldParentList && oldParentList !== newParentListRef) {
      oldParentList.updatedAt = now;
      oldParentList.lastUsedAt = now;
    }
    userState.updatedAt = now;
    userState.lastUsedAt = now;
  }
  return { task: target, userState, mutated };
}

function deleteUserTask(store, ownerKey, taskId, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.tasks[taskId];
  if (!target) {
    return false;
  }
  ensureTaskCollections(target);
  for (const childId of [...target.childTaskIds]) {
    deleteUserTask(store, ownerKey, childId, { fallbackKeys });
  }
  if (target.parentTaskId) {
    const parentTask = userState.tasks[target.parentTaskId];
    if (parentTask) {
      ensureTaskCollections(parentTask);
      removeFromArray(parentTask.childTaskIds, target.id);
      const timestamp = new Date().toISOString();
      parentTask.updatedAt = timestamp;
      parentTask.lastUsedAt = timestamp;
    }
  } else if (target.listId) {
    const parentList = userState.lists[target.listId];
    if (parentList) {
      ensureListCollections(parentList);
      removeFromArray(parentList.taskIds, target.id);
      const timestamp = new Date().toISOString();
      parentList.updatedAt = timestamp;
      parentList.lastUsedAt = timestamp;
    }
  }
  delete userState.tasks[taskId];
  const now = new Date().toISOString();
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  return true;
}

// T122 W5 — Routine counter operations
function incrementUserTaskCounter(store, ownerKey, taskId, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.tasks[taskId];
  if (!target) throw new Error('Task not found.');
  if (target.type !== 'routine' && target.type !== 'round') {
    throw new Error('Task is not a routine or round — cannot increment counter.');
  }
  const current = target.currentCount || 0;
  const tc = target.targetCount;
  const newCount = tc ? Math.min(current + 1, tc) : current + 1;
  target.currentCount = newCount;
  const now = new Date().toISOString();
  target.updatedAt = now;
  target.lastUsedAt = now;
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  const completed = tc && newCount >= tc;
  if (completed) {
    target.isCompleted = true;
    target.completedAt = now;
  }
  return { task: target, userState, completed: !!completed };
}

function setUserTaskTargetCount(store, ownerKey, taskId, targetCount, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.tasks[taskId];
  if (!target) throw new Error('Task not found.');
  const tc = targetCount === null || targetCount === 0 ? null : Math.trunc(Math.max(1, Number(targetCount)));
  target.targetCount = tc;
  if (tc && (target.currentCount || 0) > tc) {
    target.currentCount = tc;
  }
  const now = new Date().toISOString();
  target.updatedAt = now;
  target.lastUsedAt = now;
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  return { task: target, userState };
}

function deleteUserTaskList(store, ownerKey, listId, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  const target = userState.lists[listId];
  if (!target) {
    return false;
  }
  ensureListCollections(target);
  for (const childListId of [...target.childListIds]) {
    deleteUserTaskList(store, ownerKey, childListId, { fallbackKeys });
  }
  for (const taskId of [...target.taskIds]) {
    deleteUserTask(store, ownerKey, taskId, { fallbackKeys });
  }
  if (target.parentId) {
    const parent = userState.lists[target.parentId];
    if (parent) {
      ensureListCollections(parent);
      removeFromArray(parent.childListIds, target.id);
      const timestamp = new Date().toISOString();
      parent.updatedAt = timestamp;
      parent.lastUsedAt = timestamp;
    }
  } else {
    removeFromArray(userState.rootListIds, target.id);
  }
  delete userState.lists[listId];
  const now = new Date().toISOString();
  userState.updatedAt = now;
  userState.lastUsedAt = now;
  return true;
}

module.exports = {
  createUserTaskList,
  createUserTask,
  createUserRoutine,
  updateUserTaskList,
  updateUserTask,
  deleteUserTask,
  incrementUserTaskCounter,
  setUserTaskTargetCount,
  deleteUserTaskList,
  MAX_ROUTINE_DEPTH,
  WORK_GRAPH_REF_FIELDS,
  DEFAULT_WORK_GRAPH,
};
